import Cookies from 'js-cookie';
import 'isomorphic-fetch';
import {
    HTTP_METHODS,
    HTTP_RESPONSE_CODE,
    DEFAULT_MIN_RETRY_DELAY_MS,
    DEFAULT_MAX_RETRY_ATTEMPTS,
} from '../../constants/http';
import {
    ERROR_STATUS,
    LANTERN_ERROR_CODES,
    BROWSER_ERROR_MESSAGES,
    RETRIABLE_ERRORS,
    RESULTS_ERRORS,
} from '../../constants/error';
import { redirectToAR } from '../../trc/troUtils';
import selectors from '../../rootSelectors';
import {
    saveResultToStorage,
    saveContentToStorage,
} from '../persistence/persistanceActions';

import {
    startApiCall,
    apiCallSucceeded,
    startRetriableGet,
    retriableGetComplete,
    retriableGetFailed,
    networkError,
    apiResponseError,
    setBackgroundRetryId,
} from './apiCallActions';
import { constructResponseError } from './apiUtils';
import { checkRefreshToken } from '../keycloak/keycloakUtils';
import keycloak from '../keycloak/keycloak';
import { trackTroError } from '../../trc/trcReporting';
import { getOfflineContent } from "../persistence/persistanceUtils";

export const getCSRFToken = () => Cookies.get('session_nonce');

const defaultGetDelay = prevDelay => prevDelay * 2;

const constructNetworkError = original => {
    let statusCode = LANTERN_ERROR_CODES.UNKNOWN_ERROR;
    const originalMessage = original.message.toLowerCase();
    if (
        !window.navigator.onLine ||
        originalMessage.includes(BROWSER_ERROR_MESSAGES.FAILED_TO_FETCH) ||
        originalMessage.includes(BROWSER_ERROR_MESSAGES.APPEARS_OFFLINE) ||
        originalMessage.includes(BROWSER_ERROR_MESSAGES.CANCELLED) ||
        originalMessage.includes(BROWSER_ERROR_MESSAGES.TIMED_OUT) ||
        originalMessage.includes(BROWSER_ERROR_MESSAGES.LOAD_FAILED)
    ) {
        statusCode = LANTERN_ERROR_CODES.NO_CONNECTION;
    }
    return Object.assign(original, {
        status: ERROR_STATUS.NETWORK_ERROR,
        statusCode,
    });
};

const handleResultsApiError = (endpoint, requestConfig, response) => dispatch => {
    const { method } = requestConfig;
    if (RESULTS_ERRORS.includes(response.statusCode) || RESULTS_ERRORS.includes(response.status)) {
        dispatch(apiResponseError(endpoint, requestConfig, response));
    } else if (method === HTTP_METHODS.POST) {
        // This will cause needed message with raised hand with notification
        // for offline result
        dispatch(apiResponseError(endpoint, requestConfig, Object.assign(response, {
            status: LANTERN_ERROR_CODES.OFFLINE_RESULT,
        })));
    }
};

/**
 * Generic function for making an API request
 * @param {str} endpoint - the URL to which we make request
 * @param {Object} requestConfig - see for more: https://github.com/github/fetch
 * @param {boolean} redirect
 * @param {boolean} handleAllErrors - controls handling of apiResponseError action invocation
 *                                    application handles only 400 and 409 error used for MAZE offline results
 * @returns {Promise}
 */
export const makeAPIRequest = (endpoint, requestConfig, redirect = false, handleAllErrors = true) => (dispatch, getStore) => {
    dispatch(startApiCall(endpoint, requestConfig));
    return checkRefreshToken().then(() => fetch(
        endpoint,
        {
            ...requestConfig,
            headers: {
                ...(requestConfig.headers || {}),
                'Authorization': `Bearer ${keycloak.token}`,
            }
        }
    )).then(response => {
        let responsePromise;
        if (response.ok) {
            dispatch(apiCallSucceeded(endpoint, requestConfig));
            if (response.status === HTTP_RESPONSE_CODE.NO_CONTENT) {
                responsePromise = Promise.resolve({});
            } else if (response.headers.get('content-type').includes('application/json')) {
                responsePromise = response.json();
            } else {
                responsePromise = response.text();
            }

            return responsePromise;
        }

        const state = getStore();
        const hasRedirectToArConditions = selectors.getExternalURLSourceStatus(state)
            && (response.status === HTTP_RESPONSE_CODE.NO_CONTENT || !response.ok)
            // if PATCH, redirect will happen via makeApiPatchRetryRequest
            && requestConfig.method !== HTTP_METHODS.PATCH;
        if (hasRedirectToArConditions) {
            const queryParams = {
                hasTroError: true,
                benchmarkMode: selectors.getisBenchmarkModeEnabled(state),
            };
            dispatch(trackTroError(`API Error - Response Code: ${response.status}`))
                .then(() => redirectToAR(queryParams));
        }

        const error = constructResponseError(response);
        if (!redirect && handleAllErrors) {
            dispatch(apiResponseError(endpoint, requestConfig, response, error));
        }
        // Should handle errors for results created in offline mode
        if (!handleAllErrors) {
            dispatch(handleResultsApiError(endpoint, requestConfig, response));
        }
        return Promise.reject(error);
    }, caught => {
        const error = constructNetworkError(caught);
        if (handleAllErrors) {
            dispatch(networkError(endpoint, requestConfig, error));
        }
        // Should handle errors for results created in offline mode
        if (!handleAllErrors) {
            dispatch(handleResultsApiError(endpoint, requestConfig, error));
        }
        return Promise.reject(error);
    });
};

export const getGetConfig = () => ({
    method: HTTP_METHODS.GET,
    credentials: 'include',
});

export const makeApiGetRequest = (endpoint) => {
    return makeAPIRequest(endpoint, getGetConfig());
};

export const makeApiGetRequestWithoutErrorHandling = (endpoint) => {
    return makeAPIRequest(endpoint, getGetConfig(), false, false);
};

export const makeRetriableGetRequest = (endpoint, retryGetRequestActionName) => (dispatch) => {
    dispatch(startRetriableGet(retryGetRequestActionName));
    return dispatch(makeApiGetRequest(endpoint))
        .then((response) => {
            dispatch(retriableGetComplete(retryGetRequestActionName));
            return response;
        }, (error) => {
            dispatch(retriableGetFailed(retryGetRequestActionName));
            return Promise.reject(error);
        });
};

export const makeCacheableRetriableGetContentRequest = (endpoint, retryGetRequestActionName) => (dispatch) => {
    dispatch(startRetriableGet(retryGetRequestActionName));
    return dispatch(makeApiGetRequestWithoutErrorHandling(endpoint))
        .then((response) => {
            dispatch(retriableGetComplete(retryGetRequestActionName));
            return response;
        })
        .then((response) => {
            dispatch(saveContentToStorage(endpoint, response));
            return response;
        })
        .catch((error) => getOfflineContent(endpoint)
            .then((content) => {
                if (content) {
                    // if we found content in storage need to complete request
                    // we need it only for forms to proceed assessment
                    // for result we are going to show message window
                    dispatch(apiCallSucceeded(endpoint, getGetConfig()));
                    dispatch(retriableGetComplete(retryGetRequestActionName));
                    return content;
                }
                dispatch(retriableGetFailed(retryGetRequestActionName));
                return Promise.reject(error);
            }));
};

export const makeBulkGetRequest = (urls) => (dispatch) => {
    return Promise.all(
        urls.map(url => {
            return dispatch(makeApiGetRequest(url));
        })
    );
};

export const getPostConfig = (data) => {
    return {
        method: HTTP_METHODS.POST,
        headers: {
            Accept: 'application/json',
        },
        body: JSON.stringify(data),
        credentials: 'include',
    };
};

export const makeApiPostRequest = (endpoint, postData) => dispatch => (
    dispatch(makeAPIRequest(endpoint, getPostConfig(postData)))
);

export const makeCacheablePostResultsRequest = (endpoint, studentUid, measure, postData) => dispatch => (
    dispatch(makeAPIRequest(endpoint, getPostConfig(postData), false, false))
        .catch(( error ) => {
            const { statusCode } = error;
            // For duplicated results do not store in indexed db
            if(!RESULTS_ERRORS.includes(statusCode)) {
                dispatch(saveResultToStorage(studentUid, measure, postData));
            }
            throw error;
        })
);

export const getPatchConfig = (data) => ({
    method: HTTP_METHODS.PATCH,
    headers: {
        Accept: 'application/json',
    },
    body: JSON.stringify(data),
    credentials: 'include',
});

export const makeApiPatchRequest = (endpoint, patchData) => dispatch => (
    dispatch(makeAPIRequest(endpoint, getPatchConfig(patchData)))
);

export const makeApiDeleteRequest = (endpoint) => {
    const deleteConfig = {
        method: HTTP_METHODS.DELETE,
        headers: {
            Accept: 'application/json',
        },
        credentials: 'include',
    };
    return makeAPIRequest(endpoint, deleteConfig);
};

/**
 * Send request to url with provided config and make an attempt to re-send it
 * based on retryCondition function output.
 * @param {String} endpoint url to send request to
 * @param {Object} requestConfig config for Fetch API request
 * @param {Function} retryCondition function executed after server response to decide if retry is needed,
 *                  takes server response as a param, returns true if need to retry, false otherwise
 * @param {Number=30} [retriesLeft] max number of retry attempts after each we stop trying
 * @param {Number=300} [delay] delay in ms between retries, will be changed on each retry
 * @param {Function} getDelay function returns new delay on each retry
 * @returns {Promise} promise which resolves when request succeeds no matter on what retry attempt,
 *                  or rejected if failed
 */
export const makeAPIRetryableRequest = (
    endpoint,
    requestConfig,
    retryCondition,
    retriesLeft = DEFAULT_MAX_RETRY_ATTEMPTS,
    delay = DEFAULT_MIN_RETRY_DELAY_MS,
    getDelay = defaultGetDelay,
) => (dispatch, getStore) => {
    const retry = () =>
        new Promise(resolve => {
            const nextDelay = getDelay(delay);
            const id = setTimeout(
                () => {
                    resolve(
                        dispatch(makeAPIRetryableRequest(
                            endpoint,
                            requestConfig,
                            retryCondition,
                            retriesLeft - 1,
                            nextDelay,
                            getDelay,
                        )),
                    );
                },
                nextDelay
            );
            dispatch(setBackgroundRetryId(id));
        });

    const state = getStore();
    const redirectIfRetryErrors = selectors.getExternalURLSourceStatus(state);

    return dispatch(makeAPIRequest(endpoint, requestConfig, redirectIfRetryErrors))
        .then(response => {
            const shouldRetry = retryCondition(response);
            if (retriesLeft > 0 && shouldRetry) {
                return retry();
            }
            return Promise.resolve(response);
        })
        .catch(error => {
            const shouldRetry = retryCondition(error);
            if (retriesLeft > 0 && shouldRetry) {
                return retry();
            }
            if (retriesLeft <= 0 && redirectIfRetryErrors) {
                const queryParams = {
                    hasTroError: true,
                    benchmarkMode: selectors.getisBenchmarkModeEnabled(state),
                };
                redirectToAR(queryParams);
            }
            return Promise.reject(error);
        });
};

/**
 * Send request to url with provided config and retry on network error, timeouts and server error.
 * @param {String} endpoint url to send request to
 * @param {Object} requestConfig config for Fetch API request
 * @returns {Promise} promise which resolves when request succeeds no matter on what retry attempt,
 *                  or rejected if failed
 */
export const makeAPIRequestRetryErrors = (
    endpoint,
    requestConfig,
    retryErrors = RETRIABLE_ERRORS,
    retriesLeft = DEFAULT_MAX_RETRY_ATTEMPTS,
) => (dispatch, getStore) => {
    const retryCondition = response => {
        const state = getStore();
        const shouldRetry = selectors.getIsBackgroundRetryInProgress(state)
            || selectors.getExternalURLSourceStatus(state);

        // if no retry needed, do not
        if (!shouldRetry) {
            return false;
        }

        // if it's a retriable error, do retry
        if (response && response.statusCode && retryErrors.includes(response.statusCode)) {
            return true;
        }
        // it's not retriable, so stop retrying
        return false;
    };

    return dispatch(makeAPIRetryableRequest(endpoint, requestConfig, retryCondition, retriesLeft));
};

export const makeApiPatchRetryRequest = (
    endpoint,
    patchData,
    retryErrors,
    retriesLeft,
) => dispatch => (
    dispatch(makeAPIRequestRetryErrors(endpoint, getPatchConfig(patchData), retryErrors, retriesLeft))
);
